import { useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import { Chat, Close } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";

import { Comments, DataLayout, FileUpload, Log, StatusChip } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";
import { useGetQueryDetails } from "../mutation";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const QueryHeader = ({ queryId, closeDrawer }: { queryId: string | null, closeDrawer: () => void }) => {

    const { queryDetails, isFetching, isPending } = useGetQueryDetails({ queryId: queryId! });

    if (isFetching || isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box sx={{ padding: 2, display: 'flex', justifyContent: 'start', bgcolor: 'action.hover', alignItems: 'center', gap: 2, width: '100%' }}>
            <Box p={1}>
                <Chat />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: .5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.primary">{queryDetails?.project_title}</Typography>
                    <StatusChip color="amber" label={queryDetails?.data?.status ? queryDetails?.status : ""} />
                </Box>
                <Typography variant="caption" color="text.primary">{queryDetails?.description}</Typography>
            </Box>
            <IconButton onClick={() => { closeDrawer() }} sx={{ ml: 'auto' }}>
                <Close sx={{ color: 'action.active' }} />
            </IconButton>
        </Box>
    );

};

export const QueryDetails: React.FC<{ queryId: string | null, onResolved: () => void, onEdit: () => void }> = ({ queryId, onResolved, onEdit }) => {

    const { can } = useCapabilities();
    const isAllowedToEditquery = can("operational_delivery:project:query:edit");

    const { queryDetails, isFetching, isPending } = useGetQueryDetails({ queryId: queryId! });

    const tabs = [
        { value: "overview", label: "Overview" },
        { value: "comments", label: "comments" },
        { value: "log", label: "log" },
    ];

    const [currentTab, setCurrentTab] = useState("overview");
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    if (isFetching || isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
            <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" alignContent="center">
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: "auto" }}
                    >
                        {tabs.map((t) => (
                            <Tab
                                key={t.value}
                                sx={{ height: "auto", minHeight: "auto" }}
                                label={t.label}
                                value={t.value}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Divider />
                <Box flex={1} overflow="hidden">
                    <TabPanel value="overview" sx={{ p: 0, height: "100%" }}>
                        <Box height={'100%'} display={'flex'} flexDirection={'column'}>
                            <Box p={2} display="flex" flexDirection="column" gap={2} flex={1}>
                                <DataLayout title={"Query Details"}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                                        {
                                            [
                                                { label: 'Query ID', value: queryDetails?.id },
                                                { label: 'Title', value: queryDetails?.title },
                                                { label: 'Description', value: queryDetails?.description },
                                                { label: 'Category', value: queryDetails?.status },
                                                { label: 'Raised By', value: queryDetails?.raised_by },
                                                { label: 'Assigned to', value: queryDetails?.assigned_to },
                                                { label: 'Date Submitted', value: queryDetails?.submitted_date ? formatDate(queryDetails?.submitted_date) : '-' }
                                            ].map((item, index) => (
                                                <Grid key={index} size={12} container>
                                                    <Grid size={6}>
                                                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                                    </Grid>
                                                    <Grid size={6}>
                                                        <Typography variant="body2" color="text.primary">{item.value}</Typography>
                                                    </Grid>
                                                </Grid>
                                            ))
                                        }
                                    </Box>
                                </DataLayout>
                                <DataLayout title={"Additional Details "}>
                                    <Box display={'flex'} flexDirection={'column'} p={2} gap={2}>
                                        <FileUpload type={"queries"} typeId={queryDetails?.id} isDisableEdit={true} isDisableDelete={true} />
                                    </Box>
                                </DataLayout>
                            </Box>
                            <Stack
                                sx={{
                                    flexShrink: 0,
                                    padding: 2,
                                    bgcolor: 'action.hover'
                                }}
                                flexDirection={'row'}
                                justifyContent={'flex-end'}
                                alignItems={'center'}
                                columnGap={2}
                            >
                                {
                                    queryDetails?.status !== 'Closed' && (
                                        <Button variant="outlined" onClick={() => onResolved()}>mark resolved</Button>
                                    )
                                }
                                {
                                    isAllowedToEditquery && (
                                        <Button variant="outlined" onClick={() => onEdit()}>edit Query</Button>
                                    )
                                }
                            </Stack>
                        </Box>
                    </TabPanel>
                    <TabPanel value="comments" sx={{ p: 2, height: "100%" }}>
                        <Comments topic={['QUERIES_COMMENT_ADDED']} sub_event={['QUERIES_AND_RFIS']} object_id={queryDetails?.id} parent_id={""} />
                    </TabPanel>
                    <TabPanel value="log" sx={{ pl: 2, py: 2, pr: 0, height: "100%" }}>
                        <Log topic={['QUERIES_AND_RFIS']} sub_event={[]} object_id={queryDetails?.id} parent_id={""} />
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    )
};